import { useEffect } from 'react';
import { applySystemActivity } from '@/features/presence/systemActivity';
import { desktopBridge } from '../bridge';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * OS-LEVEL IDLE, SUBSCRIBED.
 *
 * A NO-OP IN A BROWSER, and that is the design rather than a shortcoming: there
 * is no web API for system-wide idle, so a tab keeps measuring Away from its
 * own DOM events (`useActivityMonitor`) and this hook never fires. Everything
 * it adds is additive to that path, never a replacement for it.
 *
 * PUSH AND PULL, like the call-activity bus and the token broker, and for the
 * same reason: a workspace that reloads is a fresh document that missed every
 * push. Without the mount read it would start from "nobody has told me
 * anything", age its own idle clock from the DOM, and spend the next five
 * minutes reporting a user Away who never left - the exact failure the shell
 * reports exist to prevent, reintroduced by a Ctrl+R.
 *
 * `systemActivity` is OPTIONAL on the bridge. The shell loads a REMOTE
 * workspace precisely so the two can ship apart, so a new bundle regularly runs
 * inside an older shell; one that cannot answer leaves the browser path in
 * place instead of throwing into a swallowing catch.
 */
export function useSystemActivity(): void {
  useDesktopEvent('system-activity', (event) => {
    applySystemActivity(event.activity);
  });

  useEffect(() => pullSystemActivity(), []);
}

/**
 * ASK THE SHELL WHAT THE MACHINE IS DOING NOW, and apply it. Returns a cancel.
 *
 * Twice in a document's life: on mount, because a fresh document missed every
 * push; and after `PresenceRuntime` resets one person's evidence for the next,
 * because the reset drops the lock and the OS reading with it and a locked or
 * idle machine pushes nothing to put them back.
 */
export function pullSystemActivity(): () => void {
  const bridge = desktopBridge();
  if (bridge?.systemActivity === undefined) return () => undefined;

  let cancelled = false;
  void bridge
    .systemActivity()
    .then((activity) => {
      /* `null` is "the shell has not read the OS yet", NOT "idle". Asserting
         anything from it would be this document inventing an answer it was
         explicitly not given - and the first real report is at most one poll
         (2 s) away. */
      if (!cancelled && activity !== null) applySystemActivity(activity);
    })
    .catch(() => undefined);

  return () => {
    cancelled = true;
  };
}
